/**
 * Risk Assessment Utilities
 *
 * Functions for calculating and displaying risk levels for real estate investments.
 */

import { RISK_THRESHOLDS } from "@/lib/constants";
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";

export type RiskLevel = "Low" | "Moderate" | "High" | "Very High" | "Unknown";
export type RiskColor =
  | "text-green-600"
  | "text-yellow-600"
  | "text-orange-600"
  | "text-red-600"
  | "text-gray-600";
export type RiskBgColor =
  | "bg-green-600"
  | "bg-yellow-600"
  | "bg-orange-600"
  | "bg-red-600"
  | "bg-gray-600";
export type RiskBadgeColor =
  | "bg-green-100 text-green-800"
  | "bg-yellow-100 text-yellow-800"
  | "bg-orange-100 text-orange-800"
  | "bg-red-100 text-red-800"
  | "bg-gray-100 text-gray-800";

export interface RiskInfo {
  level: RiskLevel;
  color: RiskBadgeColor;
  icon:
    | typeof AlertCircle
    | typeof CheckCircle2
    | typeof AlertTriangle
    | typeof XCircle;
}

export interface RiskMetric {
  level: RiskLevel;
  color: RiskColor;
  bgColor: RiskBgColor;
  score: number;
}

export interface CapRateRisk {
  level: "Excellent" | "Good" | "Fair" | "Low";
  color: RiskColor;
}

/**
 * Get risk level information based on deal score
 */
export function getRiskLevel(score: number | null | undefined): RiskInfo {
  if (!score) {
    return {
      level: "Unknown",
      color: "bg-gray-100 text-gray-800",
      icon: AlertCircle,
    };
  }
  if (score >= RISK_THRESHOLDS.DEAL_SCORE.EXCELLENT) {
    return {
      level: "Low",
      color: "bg-green-100 text-green-800",
      icon: CheckCircle2,
    };
  }
  if (score >= RISK_THRESHOLDS.DEAL_SCORE.FAIR) {
    return {
      level: "Moderate",
      color: "bg-yellow-100 text-yellow-800",
      icon: AlertTriangle,
    };
  }
  return { level: "High", color: "bg-red-100 text-red-800", icon: XCircle };
}

/**
 * Get investment recommendation based on deal score
 */
export function getRecommendation(score: number | null | undefined): string {
  if (!score) return "Review";
  if (score >= RISK_THRESHOLDS.DEAL_SCORE.EXCELLENT) return "Strong Buy";
  if (score >= RISK_THRESHOLDS.DEAL_SCORE.FAIR) return "Consider";
  return "Pass";
}

/**
 * Get leverage risk assessment based on LTV
 */
export function getLeverageRisk(ltv: number): RiskMetric {
  if (ltv <= RISK_THRESHOLDS.LTV.LOW) {
    return {
      level: "Low",
      color: "text-green-600",
      bgColor: "bg-green-600",
      score: 0,
    };
  }
  if (ltv <= RISK_THRESHOLDS.LTV.MEDIUM) {
    return {
      level: "Moderate",
      color: "text-yellow-600",
      bgColor: "bg-yellow-600",
      score: 30,
    };
  }
  if (ltv <= RISK_THRESHOLDS.LTV.HIGH) {
    return {
      level: "High",
      color: "text-orange-600",
      bgColor: "bg-orange-600",
      score: 60,
    };
  }
  return {
    level: "Very High",
    color: "text-red-600",
    bgColor: "bg-red-600",
    score: 90,
  };
}

/**
 * Get cash flow risk assessment based on DSCR
 */
export function getCashFlowRisk(dscr: number): RiskMetric {
  if (dscr >= RISK_THRESHOLDS.DSCR.HEALTHY) {
    return {
      level: "Low",
      color: "text-green-600",
      bgColor: "bg-green-600",
      score: 0,
    };
  }
  if (dscr >= RISK_THRESHOLDS.DSCR.LOW) {
    return {
      level: "Moderate",
      color: "text-yellow-600",
      bgColor: "bg-yellow-600",
      score: 30,
    };
  }
  if (dscr >= RISK_THRESHOLDS.DSCR.CRITICAL) {
    return {
      level: "High",
      color: "text-orange-600",
      bgColor: "bg-orange-600",
      score: 60,
    };
  }
  return {
    level: "Very High",
    color: "text-red-600",
    bgColor: "bg-red-600",
    score: 90,
  };
}

/**
 * Get cap rate risk assessment
 */
export function getCapRateRisk(
  capRate: number | undefined,
): CapRateRisk | null {
  if (!capRate) return null;
  if (capRate >= RISK_THRESHOLDS.CAP_RATE.HIGH) {
    return { level: "Excellent", color: "text-green-600" };
  }
  if (capRate >= RISK_THRESHOLDS.CAP_RATE.MEDIUM) {
    return { level: "Good", color: "text-green-600" };
  }
  if (capRate >= RISK_THRESHOLDS.CAP_RATE.LOW) {
    return { level: "Fair", color: "text-yellow-600" };
  }
  return { level: "Low", color: "text-red-600" };
}

/**
 * Get status badge color class
 */
export function getStatusColor(status: string): string {
  switch (status) {
    case "analyzing":
      return "bg-blue-100 text-blue-800";
    case "under_contract":
      return "bg-yellow-100 text-yellow-800";
    case "closed":
      return "bg-green-100 text-green-800";
    case "passed":
    case "dead":
      return "bg-gray-100 text-gray-800";
    case "active":
      return "bg-green-100 text-green-800";
    case "sold":
      return "bg-blue-100 text-blue-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * Format status for display
 */
export function formatStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
